import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/**
 * Electronic signature for the custody vouchers that already exist
 * (EmployeesAssets[Price]PDF, EmployeesAssetUnitario[Price]PDF). No format is
 * rewritten: each one takes an optional `signature` and
 *  - formats whose signature block is wired (generic, Círculo de Crédito,
 *    Círculo Laboral) put it on their own line with signedCell();
 *  - the rest get a "constancia de firma" on a NEW last page (output()), so
 *    none of their pages moves.
 * Without a signature nothing changes: signedCell() returns the same cell and
 * output() hands the same document to pdfmake (vouchersUnchanged.test.js).
 *
 * signature: {image (PNG data URL), signedAt (DD/MM/YYYY HH:mm), generatedBy,
 *   custodian (printed on the constancia)}
 */

export const signatureNote = (signature) =>
  `Firmado electrónicamente el ${signature.signedAt}. Generado por: ${signature.generatedBy}.`;

export const signedDate = (signature) => String(signature.signedAt || '').slice(0, 10);

const GAP = 4;

/**
 * The cell whose top border is the custodian's signature line, with the drawn
 * signature just above that line and the note under the name. The image is
 * detached (relativePosition): it sits in the blank space every format already
 * leaves for signing by hand, so the row keeps its height.
 */
export const signedCell = (cell, signature, { width = 150, height = 40 } = {}) => {
  if (!signature) return cell;
  const { text, ...rest } = cell;
  return {
    ...rest,
    stack: [
      {
        image: signature.image,
        fit: [width, height],
        alignment: 'center',
        relativePosition: { x: 0, y: -(height + GAP) },
      },
      { text },
      {
        text: signatureNote(signature),
        bold: false,
        fontSize: 6.5,
        color: '#6f7a82',
        alignment: 'center',
        margin: [0, 4, 0, 0],
      },
    ],
  };
};

/** A "Fecha" line (Círculo) filled with the signing date, above the line. */
export const signedDateCell = (cell, signature) => {
  if (!signature) return cell;
  const { text, ...rest } = cell;
  return {
    ...rest,
    stack: [
      {
        text: signedDate(signature),
        fontSize: 10,
        alignment: 'center',
        relativePosition: { x: 0, y: -(12 + GAP) },
      },
      { text },
    ],
  };
};

const lineLayout = {
  hLineWidth: () => 0.8,
  vLineWidth: () => 0,
  hLineColor: () => '#45535b',
};

/** Fallback for formats whose block is not wired yet: a page of its own. */
export const signatureRecord = (signature) => ({
  pageBreak: 'before',
  unbreakable: true,
  stack: [
    {
      text: 'CONSTANCIA DE FIRMA ELECTRÓNICA',
      bold: true,
      fontSize: 13,
      alignment: 'center',
      margin: [0, 10, 0, 6],
    },
    {
      text:
        'El resguardante firmó electrónicamente el vale de resguardo de las ' +
        'páginas anteriores.',
      fontSize: 10,
      alignment: 'center',
      margin: [0, 0, 0, 30],
    },
    { image: signature.image, fit: [220, 70], alignment: 'center' },
    {
      table: {
        widths: ['*', 240, '*'],
        body: [
          [
            { text: '', border: [false, false, false, false] },
            {
              text: signature.custodian || ' ',
              bold: true,
              fontSize: 10,
              alignment: 'center',
              border: [false, true, false, false],
            },
            { text: '', border: [false, false, false, false] },
          ],
        ],
      },
      layout: lineLayout,
    },
    { text: 'Resguardante', fontSize: 9, alignment: 'center', margin: [0, 2, 0, 30] },
    {
      table: {
        widths: [150, '*'],
        body: [
          [{ text: 'Fecha y hora de firma', bold: true }, { text: signature.signedAt }],
          [{ text: 'Generado por', bold: true }, { text: signature.generatedBy }],
        ],
      },
      layout: 'lightHorizontalLines',
      fontSize: 10,
    },
  ],
});

export const withSignatureRecord = (doc, signature) => ({
  ...doc,
  content: [].concat(doc.content, signatureRecord(signature)),
});

/**
 * Last step of every voucher generator, in place of pdfMake.createPdf(doc).open().
 * options: {signature?, blob? (resolve the PDF as a Blob), download? (file name)}.
 * Without options it is exactly the old call: the same document, opened.
 * @param {Object} placement - {inBlock: true} when the format already placed the
 *   signature on its own line (no constancia then).
 */
export const output = (doc, options = {}, { inBlock = false } = {}) => {
  const { signature, blob, download } = options;
  const pdf = pdfMake.createPdf(signature && !inBlock ? withSignatureRecord(doc, signature) : doc);
  if (blob) return new Promise((resolve) => pdf.getBlob(resolve));
  if (download) return pdf.download(download);
  pdf.open();
  return undefined;
};



// WEBPACK FOOTER //
// src/lib/pdfs/signature.js